import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useProfile } from '@/entities/profile';
import { ROUTES_PATH } from '@/shared/constants/routes';
import { SvgIcon } from '@/shared/ui/SvgIcon';
import { CustomTypography } from '@/shared/ui/Typography';

import {
  StyledNav,
  StyledUlAdditional,
  StyledUlMain,
} from './styles/navigationStyles';
import { NavigationItem } from './NavigationItem';
import { NavigationLink } from './NavigationLink';

export const Navigation = () => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const accountSupportId = profile?.account_support_id ?? profile?.account_id;
  const chatWindowRef = useRef<Window | null>(null);

  const handleChatsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = `/${ROUTES_PATH.MAIN.CHATS}`;

    if (chatWindowRef.current && !chatWindowRef.current.closed) {
      chatWindowRef.current.focus();
    } else {
      chatWindowRef.current = window.open(url, '_blank');
    }
  };

  return (
    <StyledNav>
      <StyledUlMain>
        <li>
          <NavigationLink
            to={`/${ROUTES_PATH.MAIN.CHATS}`}
            onClick={handleChatsClick}
            rel="noopener noreferrer"
          >
            <NavigationItem variant="square" label={t('common:menu.chats')}>
              <SvgIcon name="chat" />
            </NavigationItem>
          </NavigationLink>
        </li>
        <li>
          <NavigationLink to={`/${ROUTES_PATH.MAIN.CHANNELS}`}>
            {({ isActive }) => (
              <NavigationItem label={t('common:menu.channels')}>
                <SvgIcon name={isActive ? 'channels-active' : 'channels'} />
              </NavigationItem>
            )}
          </NavigationLink>
        </li>
        <li>
          <NavigationLink to={`/${ROUTES_PATH.MAIN.INTEGRATIONS}`}>
            {({ isActive }) => (
              <NavigationItem label={t('common:menu.integration')}>
                <SvgIcon
                  name={isActive ? 'integrations-active' : 'integrations'}
                />
              </NavigationItem>
            )}
          </NavigationLink>
        </li>
        <li>
          <NavigationLink to={`/${ROUTES_PATH.MAIN.COMPANIES}`}>
            {({ isActive }) => (
              <NavigationItem label={t('common:menu.companies')}>
                <SvgIcon
                  className="svg"
                  name={isActive ? 'companies-active' : 'companies'}
                />
              </NavigationItem>
            )}
          </NavigationLink>
        </li>
        {/* <li> */}
        {/*  /!* <NavigationLink to={`/${ROUTES_PATH.MAIN.TEMPLATES}`}> *!/ */}
        {/*  /!*  {({ isActive }) => ( *!/ */}
        {/*  <NavigationItem label={t('common:menu.templates')} disabled> */}
        {/*    /!* <SvgIcon name={isActive ? 'templates-active' : 'templates'} /> *!/ */}
        {/*    <SvgIcon name="templates" /> */}
        {/*  </NavigationItem> */}
        {/*  /!*  )} *!/ */}
        {/*  /!* </NavigationLink> *!/ */}
        {/* </li> */}
        <li>
          <NavigationLink to={`/${ROUTES_PATH.MAIN.ANALYTICS}`} end>
            {({ isActive }) => (
              <NavigationItem label={t('common:menu.analytics')}>
                <SvgIcon name={isActive ? 'analytics-active' : 'analytics'} />
              </NavigationItem>
            )}
          </NavigationLink>
        </li>
        <li>
          <NavigationLink to={`/${ROUTES_PATH.MAIN.AI_TRACE}`}>
            {({ isActive }) => (
              <NavigationItem label={t('common:menu.aiTrace')}>
                <SvgIcon name={isActive ? 'ai-trace-active' : 'ai-trace'} />
              </NavigationItem>
            )}
          </NavigationLink>
        </li>
        {profile?.account_type === 'partner' && (
          <li>
            <NavigationLink to={`/${ROUTES_PATH.MAIN.AFFILIATE}`}>
              {({ isActive }) => (
                <NavigationItem
                  label={t('common:menu.affiliate')}
                  variant="square"
                >
                  <SvgIcon name={isActive ? 'affiliate-active' : 'affiliate'} />
                </NavigationItem>
              )}
            </NavigationLink>
          </li>
        )}
      </StyledUlMain>
      <StyledUlAdditional>
        <li>
          <NavigationLink to={`/${ROUTES_PATH.MAIN.ACCOUNT_SETTINGS}`}>
            <NavigationItem
              label={t('common:menu.accountSettings')}
              variant="standard"
            >
              <SvgIcon name="settings-icon" />
            </NavigationItem>
          </NavigationLink>
          {accountSupportId ? (
            <CustomTypography
              variant="caption"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                marginLeft: '30px',
                marginTop: '4px',
                color: 'text.secondary',
                fontFamily: 'monospace',
                userSelect: 'all',
                wordBreak: 'break-all',
              }}
            >
              <span>{t('common:header.userMenu.accountId')}</span>
              <span style={{ whiteSpace: 'nowrap' }}>{accountSupportId}</span>
            </CustomTypography>
          ) : null}
        </li>
        {/* <li>
          <NavigationLink to={`/${ROUTES_PATH.MAIN.FAQ}`}>
            <NavigationItem label={t('common:menu.faq')} variant="standard">
              <SvgIcon name="faq" />
            </NavigationItem>
          </NavigationLink>
        </li> */}
      </StyledUlAdditional>
    </StyledNav>
  );
};
